import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { useVoiceRecording } from '../../../hooks/useVoiceRecording';
import type { Topic } from '../../../types/topic';
import { logger } from '../../../utils/logger';
import { getCachedUser } from '../../../utils/userCache';
import { NoteDetailPanel } from './NoteDetailPanel';
import { useViewport } from '../../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../../hooks/useStatusBarInfo';
import NoteFilterToolbar from './filters/NoteFilterToolbar';
import { useFilteredNotes } from '../../../hooks/queries/useNotes';
import { useTopicSubscription } from '../../../hooks/useTopicSubscription';
import { noteKeys } from '../../../hooks/queries/noteKeys';
import { useToastUIStore } from '../../../store/toastUIStore';
import { useNotesUIStore } from '../../../store/notesUIStore';
import { useInitialLoadingScreenManager } from '../../../hooks/useInitialLoadingScreenManager';
import { MobileHeader } from './components/MobileHeader';
import { ActionButtons } from './components/ActionButtons';
import { NotesList } from './components/NotesList';
import { useNotesOperations } from '../../../hooks/useNotesOperations';

interface NotesTopicProps {
  topic: Topic;
  hasCollaborators?: boolean;
  onBackClick: () => void;
}

export function NotesTopic({ topic, hasCollaborators = false, onBackClick }: NotesTopicProps) {
  const { isMobile, isSmallDesktop } = useViewport();
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();
  const contentRef = useRef<HTMLDivElement>(null);

  // Local component state
  const [isScrolled, setIsScrolled] = useState(false);
  const [openNoteId, setOpenNoteId] = useState<string | null>(null);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [editInput, setEditInput] = useState('');

  // UI state from Zustand store
  const {
    activeFilters,
    isNotesPanelOpen,
    selectedNoteId,
    editingNoteId,
    confirmDeleteNoteId,
    setSearchQuery,
    toggleUserFilter,
    setUserFilters,
    resetFilters,
    openNotePanel,
    closeNotePanel,
    setEditingNote,
    setConfirmDeleteNote,
  } = useNotesUIStore();

  // Voice recording state
  const {
    isRecording,
    recordingTime,
    maxRecordingTime,
    error: recordingError,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecording();

  // Use centralized operations hook
  const { createNote, updateNote, deleteNote, updateTopicTimestamp } = useNotesOperations(topic.id);

  // Remove direct mutation hooks - using centralized operations instead

  // Update topic timestamp when component mounts (topic is opened)
  useEffect(() => {
    updateTopicTimestamp();
  }, [topic.id]); // Only run when topic changes

  // Use filtered notes query with filters from the UI store
  const { data: notesData, isLoading } = useFilteredNotes(
    topic.id,
    activeFilters.searchQuery,
    activeFilters.userIds.length > 0 ? activeFilters.userIds : undefined
  );

  // Memoize notes array to prevent infinite re-renders
  const notes = useMemo(() => notesData || [], [notesData]);

  // Find the currently selected note from the notes array
  const selectedNote = notes.find(note => note.id === selectedNoteId);

  // Set up real-time subscriptions for notes
  // Memoize subscriptions to prevent unnecessary re-subscriptions
  const subscriptions = useMemo(
    () => [
      {
        table: 'notes' as const,
        queryKey: noteKeys.byTopic(topic.id),
      },
    ],
    [topic.id]
  );

  useTopicSubscription(topic.id, subscriptions);

  // Handle loading screen state with the custom hook
  const { markRouteComplete } = useInitialLoadingScreenManager();

  // Only mark as complete when loading is done
  // Using a ref to track if we've already marked complete to avoid infinite updates
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    if (!isLoading && !hasMarkedCompleteRef.current) {
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [isLoading]); // Remove markRouteComplete from deps to prevent infinite updates

  // Reset filters when topic changes
  useEffect(() => {
    resetFilters();
  }, [topic.id, resetFilters]);

  // Memoize user filters to prevent recalculation on every render
  const memoizedUserFilters = useMemo(() => {
    // Only build user filters if we have notes and project is shared
    if (notes.length === 0 || !hasCollaborators) {
      return [];
    }

    // Get unique user IDs from notes
    const userIds = [...new Set(notes.map(note => note.userId))];

    // Create UserFilter objects for each unique user
    return userIds
      .map(userId => {
        // Check if user info is available in user cache (populated by collaborator service)
        const cachedUser = getCachedUser(userId);

        // Find a note by this user to get their information
        const noteByUser = notes.find(note => note.userId === userId);

        // Check if this user is already in active filters
        const isActive = activeFilters.userIds.includes(userId);

        // Get best name available from cache or note user_info
        let name = 'Unknown User';
        let avatarUrl = null;

        if (cachedUser) {
          // Prefer cached user info (should be most reliable)
          name = cachedUser.display_name || cachedUser.email?.split('@')[0] || 'Unknown User';
          avatarUrl = cachedUser.avatar_url || null;
        } else if (noteByUser?.userInfo) {
          // Fall back to note's user_info
          name =
            noteByUser.userInfo.name || noteByUser.userInfo.email?.split('@')[0] || 'Unknown User';
          avatarUrl = noteByUser.userInfo.avatarUrl || null;
        }

        return {
          id: userId,
          name: name,
          avatar_url: avatarUrl,
          active: isActive,
        };
      })
      .filter(filter => filter.name !== 'Unknown User'); // Filter out users with no available names
  }, [notes, activeFilters.userIds, hasCollaborators]);

  // Toggle a user filter
  const handleToggleUserFilter = (userId: string, isExclusive = false) => {
    // Get current active user IDs
    const currentUserIds = [...activeFilters.userIds];

    // If exclusive, replace all user IDs with just this one (if not already active)
    if (isExclusive) {
      const isCurrentlyActive = currentUserIds.includes(userId);
      // If it's active, deselect it; otherwise, select only this user
      const newUserIds = isCurrentlyActive ? [] : [userId];
      setUserFilters(newUserIds);
      return;
    }

    // If not exclusive, toggle this user ID in the array
    toggleUserFilter(userId);
  };

  // Panel visibility animation
  useEffect(() => {
    if (isNotesPanelOpen && selectedNoteId) {
      // Small delay to ensure smooth animation
      setTimeout(() => setIsPanelVisible(true), 50);
    } else {
      setIsPanelVisible(false);
    }
  }, [isNotesPanelOpen, selectedNoteId]);

  // Add scroll listener to detect when to show header shadow
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const scrollTop = contentRef.current.scrollTop;
        setIsScrolled(scrollTop > 10);
      }
    };

    const scrollContainer = contentRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleEdit = async (noteId: string, content: string, summary?: string | null) => {
    try {
      // Update the existing note (even if empty)
      await updateNote({
        noteId,
        updates: {
          content,
          summary,
        },
      });

      // Don't update topic timestamp on every edit - it causes excessive refetching

      // Clear editing state
      setEditingNote(null);
    } catch (error) {
      logger.error('Failed to edit note:', error);
    }
  };

  const handleDelete = async (noteId: string) => {
    try {
      // Note: No longer tracking local deletes - using invalidation approach

      // Delete the note
      await deleteNote({ noteId });

      // Don't update topic timestamp on delete - it causes unnecessary refetching

      // If the deleted note was selected, close the panel
      if (selectedNoteId === noteId) {
        closeNotePanel();
      }

      // Clear delete confirmation
      setConfirmDeleteNote(null);
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const { addToast } = useToastUIStore();

  const handleVoiceMessage = async () => {
    if (isRecording) {
      try {
        const result = await stopRecording();
        const text = result?.transcription || '';

        if (text.trim()) {
          await createNote({
            content: text.trim(),
          });

          // Don't update topic timestamp on voice message - it causes unnecessary refetching
        } else {
          // If transcription returns empty, show a gentle notification
          addToast({
            type: 'info',
            message: 'No speech detected. Please try again with a clearer voice.',
          });
        }
      } catch (error) {
        console.error('Error processing voice message:', error);

        // Show user-friendly error notification
        addToast({
          type: 'error',
          message: 'Unable to process voice recording. Please try again later.',
        });

        // Ensure recording state is reset even on error
        cancelRecording();
      }
    } else {
      try {
        await startRecording();
      } catch (error) {
        console.error('Error starting recording:', error);

        // Show microphone permission error to user
        addToast({
          type: 'error',
          message: 'Microphone access denied. Please allow microphone permissions.',
        });
      }
    }
  };

  const handleNoteSelect = (note: any) => {
    openNotePanel(note.id);
    // Mark note as open as soon as it's selected
    setOpenNoteId(note.id);
  };

  const handleEditStart = (noteId: string, content: string) => {
    setEditInput(content);
    setEditingNote(noteId);
  };

  const handleCreateNote = async () => {
    try {
      // Create a real note in the database immediately
      const newNote = await createNote({
        content: '',
        summary: null,
      });

      logger.component('Notes creating new note', { noteId: newNote.id });

      // Don't update topic timestamp on create - it causes unnecessary refetching
      // Only update timestamp when explicitly opening a topic

      // Set this as the selected note and mark it as open
      openNotePanel(newNote.id);
      setOpenNoteId(newNote.id);

      // Start editing the new note
      setEditInput('');
      setEditingNote(newNote.id);
    } catch (error) {
      console.error('Failed to create new note:', error);
    }
  };

  const handleClosePanel = async () => {
    logger.component('Notes panel closing', {
      hasSelectedNote: !!selectedNoteId,
    });

    // First hide the panel with animation (do this immediately for better UX)
    setIsPanelVisible(false);
    closeNotePanel();

    // Clear any editing state
    setEditingNote(null);
    setConfirmDeleteNote(null);

    // Clear the open note state when closing
    setOpenNoteId(null);

    // After UI state is updated, check if the note should be deleted
    // This avoids race conditions by separating UI updates from data operations
    const currentNote = notes.find(note => note.id === selectedNoteId);

    if (currentNote && !currentNote.content?.trim() && !currentNote.summary?.trim()) {
      logger.component('Notes delete completely empty note on close', {
        noteId: currentNote.id,
        contentEmpty: !currentNote.content?.trim(),
        summaryEmpty: !currentNote.summary?.trim(),
      });

      // Delete the empty note silently
      await deleteNote({ noteId: currentNote.id });
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Mobile header with shadow on scroll */}
      <MobileHeader
        topicName={topic.name}
        isScrolled={isScrolled}
        isMobile={isMobile}
        isNativeApp={isNativeApp}
        statusBarHeight={statusBarHeight}
        onBackClick={onBackClick}
      />

      {/* Desktop filter toolbar - only visible on desktop */}
      {!isMobile && (
        <NoteFilterToolbar
          isMobile={isMobile}
          userFilters={memoizedUserFilters}
          toggleUserFilter={handleToggleUserFilter}
          hasCollaborators={hasCollaborators}
          searchQuery={activeFilters.searchQuery}
          onSearchChange={setSearchQuery}
        />
      )}

      {/* Main content area with relative positioning to contain the note panel */}
      <div className="flex-1 overflow-hidden relative">
        {/* Notes list */}
        <div ref={contentRef} className="h-full overflow-y-auto">
          <NotesList
            notes={notes}
            selectedNoteId={selectedNoteId}
            openNoteId={openNoteId}
            isLoading={isLoading}
            isMobile={isMobile}
            isSmallDesktop={isSmallDesktop}
            searchQuery={activeFilters.searchQuery}
            onSearchChange={setSearchQuery}
            onNoteSelect={handleNoteSelect}
          />
        </div>

        {/* Background overlay when detail panel is open */}
        <div
          className={cn(
            'fixed inset-0 bg-black transition-opacity duration-500',
            isPanelVisible
              ? 'bg-opacity-25 z-40 opacity-100'
              : 'bg-opacity-0 z-0 opacity-0 pointer-events-none'
          )}
          onClick={handleClosePanel} // Close panel when clicking overlay
        />

        {/* Detail panel (slides in from the right) */}
        {selectedNote && (
          <NoteDetailPanel
            note={selectedNote}
            onClose={handleClosePanel}
            onEdit={handleEdit}
            onDelete={handleDelete}
            visible={isPanelVisible}
            isEditing={editingNoteId === selectedNote.id}
            messageToDelete={confirmDeleteNoteId === selectedNote.id ? selectedNote.id : null}
            editInput={editInput}
            onEditStart={handleEditStart}
            onEditChange={setEditInput}
            onEditCancel={() => setEditingNote(null)}
            onEditSubmit={noteId => handleEdit(noteId, editInput)}
            onDeleteClick={noteId => setConfirmDeleteNote(noteId)}
            onCancelDelete={() => setConfirmDeleteNote(null)}
          />
        )}
      </div>

      {/* Fixed action buttons at the bottom */}
      {!selectedNoteId && (
        <ActionButtons
          isRecording={isRecording}
          recordingTime={recordingTime}
          maxRecordingTime={maxRecordingTime}
          recordingError={recordingError}
          onVoiceMessage={handleVoiceMessage}
          onCreateNote={handleCreateNote}
          onCancelRecording={cancelRecording}
        />
      )}
    </div>
  );
}
